import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { IMG } from "@/lib/images";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { pageSEO } from "@/lib/seo";
import { useLang } from "@/i18n/LanguageContext";
import api from "@/lib/api";

// Parse editorial date strings (FR/IT/EN month + year) into a sortable number.
const MONTHS = {
  janvier: 1, février: 2, fevrier: 2, mars: 3, avril: 4, mai: 5, juin: 6, juillet: 7, août: 8, aout: 8, septembre: 9, octobre: 10, novembre: 11, décembre: 12, decembre: 12,
  gennaio: 1, febbraio: 2, marzo: 3, aprile: 4, maggio: 5, giugno: 6, luglio: 7, agosto: 8, settembre: 9, ottobre: 10, novembre_it: 11, dicembre: 12,
  january: 1, february: 2, march: 3, may: 5, june: 6, july: 7, august: 8, september: 9, october: 10, december: 12,
};
const dateKey = (post) => {
  const s = (post.date || "").toLowerCase();
  const yearMatch = s.match(/\d{4}/);
  const year = yearMatch ? parseInt(yearMatch[0], 10) : 0;
  let month = 0;
  for (const [name, num] of Object.entries(MONTHS)) {
    if (s.includes(name.replace("_it", ""))) { month = Math.max(month, num); break; }
  }
  return year * 100 + month;
};

export default function Journal() {
  const { c, lang } = useLang();
  const j = c.pages.journal;
  const [cat, setCat] = useState(c.journal.categories[0]);
  const [posts, setPosts] = useState([]);

  useEffect(() => { api.get("/journal").then((r) => setPosts([...r.data].sort((a, b) => dateKey(b) - dateKey(a)))).catch(() => {}); }, []);
  useEffect(() => { setCat(c.journal.categories[0]); }, [c]);

  const catMap = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 }; // index alignment
  const catIndex = c.journal.categories.indexOf(cat);
  // Categories in stored data are French; map filter position to French category label
  const frCats = ["Tous", "Design", "Mariage", "Conseils", "Tendances"];
  const activeFr = frCats[catIndex] || "Tous";
  const filtered = activeFr === "Tous" ? posts : posts.filter((p) => p.category === activeFr);

  return (
    <div data-testid="journal-page">
      <SEO {...pageSEO("journal", lang)} path="/journal" lang={lang} image={IMG.journal1} />
      <PageHeader eyebrow={j.eyebrow} lines={j.hLines} title="Journal" subtitle={j.subtitle} image={IMG.journal1} />
      <section className="py-16 lg:py-24 bg-olime-bg">
        <div className="container-olime">
          <div className="flex flex-wrap gap-3 mb-14">
            {c.journal.categories.map((cc) => (
              <button key={cc} data-testid={`journal-filter-${cc.toLowerCase()}`} onClick={() => setCat(cc)}
                className={`px-5 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${cat === cc ? "bg-olime-ink text-olime-bg border-olime-ink" : "border-olime-line text-olime-muted hover:border-olime-ink hover:text-olime-ink"}`}>
                {cc}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {filtered.map((post, i) => (
              <Reveal key={post.slug} delay={(i % 3) * 0.08}>
                <Link to={`/journal/${post.slug}`} data-testid={`journal-article-${post.slug}`} className="group block">
                  <div className="overflow-hidden"><img src={post.image} alt={post.title} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" /></div>
                  <p className="eyebrow mt-5">{post.category} • {post.date}</p>
                  <h3 className="font-serif text-2xl mt-2 leading-snug">{post.title}</h3>
                  <p className="text-sm text-olime-muted mt-2">{post.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
